import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, Briefcase, Shield, FileDown, Loader2, AlertTriangle, ChevronLeft, ChevronRight, Calendar, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FileAnalysisResultDto, Incarico, UserRole } from '../types';
import { IncaricoCard } from '../components/mieiIncarichi/IncaricoCard';
import { TurnoCard } from '../components/mieiIncarichi/TurnoCard';
import { generaRiepilogoCollab } from '../services/generaRiepilogoCollaboratore';
import { UrgenzaModal } from '../components/mieiIncarichi/UrgenzaModal';
import { IncaricoService, RelazioneGiornalieraService, ServizioFiduciarioService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContext';
import { UploadSummaryModal } from '../components/UploadSummaryModal';
import { localIsoDate } from '../utils/utils';

interface Props {
    viewMode: 'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE';
    setViewMode: (m: 'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE') => void;
    filteredIncarichi: Incarico[];
    filteredTurni: any[];
    allIncarichi?: Incarico[];
    onSelectIncarico: (id: number) => void;
    onSelectTurno: (id: number) => void;
    onRefresh: (background?: boolean) => void;
    formatDateTime: (dateStr: string) => string;
    onUrgenzaCreated: () => void;
    pendingFutureCount?: number;
    // Totale incarichi+turni ASSEGNATO in attesa di accettazione, per il badge
    // del tab "Da accettare" visibile anche dagli altri tab.
    pendingDaAccettareCount?: number;
}

const hasRealSoggetto = (inc: Incarico): boolean => {
    // Gli ASSEGNATO restano visibili anche senza soggetto registrato, perché il
    // collaboratore deve poterli accettare o rifiutare. I RIFIUTATI restano come
    // traccia con card oscurata ("Mandato #N") e dettaglio bloccato dal BE.
    if (inc.stato === 'ASSEGNATO' || inc.stato === 'RIFIUTATO') return true;
    // Solo i mandati investigativi hanno un soggetto persona. Bonifiche e consulenze
    // non ce l'hanno e vanno mostrate comunque.
    const modelId = (inc.pratica as any)?.modelId;
    if (modelId && modelId !== 'mand-investigativo') return true;
    if (inc.soggettoNome) return true;
    try {
        const raw = (inc.pratica as any)?.datiAggiuntiviJson;
        const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
        return !!(dati.soggetti?.[0]?.nome || dati.soggetto_nome || dati.soggetto_denominazione);
    } catch { return false; }
};

type ViewMode = 'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE';

const EMPTY_MSG: Record<ViewMode, { incarichi: string; turni: string }> = {
    OGGI: { incarichi: 'Nessuna investigazione attiva per oggi.', turni: 'Nessun turno da effettuare oggi.' },
    FUTURI: { incarichi: 'Nessuna investigazione programmata per il futuro.', turni: 'Nessun turno in programma per i prossimi giorni.' },
    STORICO: { incarichi: 'Nessun archivio storico presente.', turni: 'Nessuno storico turni.' },
    DA_ACCETTARE: { incarichi: 'Nessuna investigazione in attesa di accettazione.', turni: 'Nessun turno in attesa di accettazione.' },
};

const SECTION_LABEL: Record<ViewMode, { incarichi: string; turni: string }> = {
    OGGI: { incarichi: 'Indagini di Oggi', turni: 'Turni di Oggi' },
    FUTURI: { incarichi: 'Indagini Future', turni: 'Turni Futuri' },
    STORICO: { incarichi: 'Indagini Archiviate', turni: 'Turni Effettuati' },
    DA_ACCETTARE: { incarichi: 'Investigazioni da Accettare', turni: 'Turni da Accettare' },
};

export const AgendaListView: React.FC<Props> = ({
    viewMode, setViewMode, filteredIncarichi, filteredTurni, allIncarichi,
    onSelectIncarico, onSelectTurno, onRefresh, formatDateTime, onUrgenzaCreated,
    pendingFutureCount, pendingDaAccettareCount,
}) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    // Titolare e responsabile raggiungono Spese e Ore da qui: nella sidebar la voce c'è solo per il collaboratore.
    const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;
    const { addToast } = useToast();
    const [isUrgenzaOpen, setIsUrgenzaOpen] = useState(false);
    const [showRiepilogoPanel, setShowRiepilogoPanel] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfMonth, setPdfMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    // Media "orfani": caricati ma senza decisione (DUPLICATE / OUTSIDE_RANGE non
    // inviati, NO_EXIF senza data), di solito per chiusura anticipata del modale
    // upload. Li riproponiamo nello stesso UploadSummaryModal (/api/media/orfani).
    const [orfaniResults, setOrfaniResults] = useState<FileAnalysisResultDto[]>([]);
    const [showOrfaniModal, setShowOrfaniModal] = useState(false);

    const loadOrfani = useCallback(async () => {
        try {
            const list = await IncaricoService.getOrfani();
            setOrfaniResults(Array.isArray(list) ? list : []);
        } catch {
            // Se l'endpoint non risponde non blocchiamo la lista incarichi.
            setOrfaniResults([]);
        }
    }, []);

    useEffect(() => { loadOrfani(); }, [loadOrfani]);

    // Storico paginato: API dedicata (GET /api/incarichi/me/storico) con filtro
    // date. Default ultimi 30 giorni, 25 elementi per pagina.
    type StoricoPreset = '30d' | '90d' | '365d' | 'custom';
    const today = useMemo(() => new Date(), []);
    const isoDateOnly = (d: Date) => localIsoDate(d);
    const daysAgo = (n: number): string => {
        const d = new Date(today);
        d.setDate(d.getDate() - n);
        return isoDateOnly(d);
    };
    const [storicoPreset, setStoricoPreset] = useState<StoricoPreset>('30d');
    const [storicoFrom, setStoricoFrom] = useState<string>(daysAgo(30));
    const [storicoTo, setStoricoTo] = useState<string>(isoDateOnly(today));
    const [storicoPage, setStoricoPage] = useState(0);
    const [storicoData, setStoricoData] = useState<Incarico[]>([]);
    const [storicoTotalPages, setStoricoTotalPages] = useState(0);
    const [storicoTotalElements, setStoricoTotalElements] = useState(0);
    const [storicoLoading, setStoricoLoading] = useState(false);
    const STORICO_PAGE_SIZE = 25;

    // Un preset aggiorna le date e torna a pagina 1; "custom" lascia le date all'utente.
    const applyPreset = useCallback((preset: StoricoPreset) => {
        setStoricoPreset(preset);
        setStoricoPage(0);
        const t = isoDateOnly(today);
        if (preset === '30d') { setStoricoFrom(daysAgo(30)); setStoricoTo(t); }
        else if (preset === '90d') { setStoricoFrom(daysAgo(90)); setStoricoTo(t); }
        else if (preset === '365d') { setStoricoFrom(daysAgo(365)); setStoricoTo(t); }
        // 'custom': date scelte dall'utente
    }, [today]);

    // Carica lo storico all'ingresso nel tab o quando cambiano date/pagina.
    useEffect(() => {
        if (viewMode !== 'STORICO') return;
        let cancelled = false;
        setStoricoLoading(true);
        IncaricoService.getMyIncarichiStorico({
            page: storicoPage,
            size: STORICO_PAGE_SIZE,
            from: storicoFrom,
            to: storicoTo,
        }).then((res: any) => {
            if (cancelled) return;
            const content = Array.isArray(res?.content) ? res.content : [];
            setStoricoData(content);
            setStoricoTotalPages(Number(res?.totalPages) || 0);
            setStoricoTotalElements(Number(res?.totalElements) || 0);
        }).catch(() => {
            if (!cancelled) {
                setStoricoData([]);
                setStoricoTotalPages(0);
                setStoricoTotalElements(0);
                addToast('Errore caricamento storico', 'error');
            }
        }).finally(() => { if (!cancelled) setStoricoLoading(false); });
        return () => { cancelled = true; };
    }, [viewMode, storicoFrom, storicoTo, storicoPage, addToast]);

    // Riepilogo mensile collaboratore (generaRiepilogoCollab): incarichi e servizi
    // fiduciari in due tabelle. Relazioni e turni si caricano in parallelo; se una
    // chiamata fallisce la sezione resta vuota e avvisiamo solo se lo sono entrambe.
    const handleDownloadRiepilogo = async () => {
        setPdfLoading(true);
        try {
            const [resRel, resTurni] = await Promise.allSettled([
                RelazioneGiornalieraService.getMie(),
                ServizioFiduciarioService.getMyTurni(),
            ]);
            const allRel: any[] = resRel.status === 'fulfilled'
                ? (Array.isArray(resRel.value) ? resRel.value : ((resRel.value as any)?.data ?? []))
                : [];
            const allTurni: any[] = resTurni.status === 'fulfilled'
                ? (Array.isArray((resTurni.value as any)?.data) ? (resTurni.value as any).data
                    : (Array.isArray(resTurni.value) ? resTurni.value as any : []))
                : [];

            // Nel PDF entrano solo i report approvati; i turni invece tutti, perché la
            // traccia operativa esiste indipendentemente dall'approvazione delle ore.
            const monthRelazioni = allRel.filter((r: any) =>
                r.stato === 'APPROVATO'
                && typeof r.dataRelazione === 'string'
                && r.dataRelazione.startsWith(pdfMonth)
            );
            const monthTurni = allTurni.filter((t: any) =>
                typeof t.dataTurno === 'string' && t.dataTurno.startsWith(pdfMonth)
            );
            const nomeCompleto = user
                ? `${user.name || ''} ${user.surname || ''}`.trim() || 'Collaboratore'
                : 'Collaboratore';
            if (monthRelazioni.length === 0 && monthTurni.length === 0) {
                addToast('Nessun report approvato o turno nel mese selezionato', 'info');
                return;
            }
            generaRiepilogoCollab(monthRelazioni, monthTurni, pdfMonth, nomeCompleto);
        } catch {
            addToast('Errore nella generazione del riepilogo', 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    // In STORICO usiamo i dati paginati dal BE, con lo stesso filtro hasRealSoggetto
    // di OGGI/FUTURI.
    const visibleIncarichi = useMemo(() => {
        const source = viewMode === 'STORICO' ? storicoData : filteredIncarichi;
        return source.filter(hasRealSoggetto);
    }, [viewMode, storicoData, filteredIncarichi]);

    const modeBtn = (mode: ViewMode, label: string) => {
        const activeColor =
            mode === 'OGGI' ? 'text-blue-700' :
            mode === 'FUTURI' ? 'text-emerald-700' :
            mode === 'DA_ACCETTARE' ? 'text-amber-700' :
            'text-slate-900';
        const showDot = mode === 'FUTURI' && !!pendingFutureCount && pendingFutureCount > 0;
        const badgeCount = mode === 'DA_ACCETTARE' ? (pendingDaAccettareCount || 0) : 0;
        return (
            <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`relative flex-1 min-w-fit sm:px-6 py-2 text-sm font-bold rounded-md transition-all ${viewMode === mode ? `bg-white ${activeColor} shadow-sm` : 'text-slate-500 hover:text-slate-700'}`}
            >
                {label}
                {showDot && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full" />
                )}
                {badgeCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-black bg-amber-500 text-white rounded-full align-middle">
                        {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                )}
            </button>
        );
    };

    return (
        <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <header className="pt-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Agenda Operativa</h1>
                    <p className="text-slate-500 mt-1">Seleziona il servizio per gestirlo.</p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                    {orfaniResults.length > 0 && (
                        <button
                            onClick={() => setShowOrfaniModal(true)}
                            title="File caricati ma rimasti in attesa di decisione (duplicati, fuori periodo, senza data)"
                            className="bg-amber-500 hover:bg-amber-400 text-white px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-amber-500/30"
                        >
                            <AlertTriangle size={18} />
                            Media da rivedere
                            <span className="ml-1 text-[11px] font-black bg-white/25 px-1.5 py-0.5 rounded-full">{orfaniResults.length}</span>
                        </button>
                    )}
                    <button
                        onClick={() => setIsUrgenzaOpen(true)}
                        className="bg-rose-600 hover:bg-rose-500 text-white px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-rose-600/30"
                    >
                        <Plus size={18} /> Incarico Urgente
                    </button>
                    {isStaff && (
                        <button
                            onClick={() => navigate('/le-mie-spese')}
                            className="bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-sm border border-slate-200"
                        >
                            <Receipt size={18} /> Spese e Ore
                        </button>
                    )}
                    {/* Tab sempre su 2 righe × 2 colonne. */}
                    <div className="grid grid-cols-2 gap-1 bg-slate-200/60 p-1 rounded-lg w-full shadow-inner">
                        {modeBtn('DA_ACCETTARE', 'Da accettare')}
                        {modeBtn('OGGI', 'Oggi')}
                        {modeBtn('FUTURI', 'Futuri')}
                        {modeBtn('STORICO', 'Storico')}
                    </div>
                    <button
                        onClick={() => setShowRiepilogoPanel(v => !v)}
                        title="Riepilogo mensile ore e km"
                        className="p-2.5 text-slate-500 hover:text-brand-700 bg-white rounded-lg shadow-sm border border-slate-200 transition-colors flex justify-center"
                    >
                        <FileDown size={20} />
                    </button>
                    <button
                        onClick={() => onRefresh(false)}
                        className="p-2.5 text-slate-500 hover:text-slate-800 bg-white rounded-lg shadow-sm border border-slate-200 transition-colors flex justify-center"
                    >
                        <RefreshCw size={20} />
                    </button>
                </div>
            </header>

            {/* Pannello riepilogo mensile */}
            {showRiepilogoPanel && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">Riepilogo Mensile Ore &amp; Km</p>
                        <p className="text-xs text-slate-400 mt-0.5">Genera un PDF con tutti i tuoi incarichi del mese, ore lavorate e km percorsi.</p>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <input
                            type="month"
                            value={pdfMonth}
                            onChange={e => setPdfMonth(e.target.value)}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-700/30"
                        />
                        <button
                            onClick={handleDownloadRiepilogo}
                            disabled={pdfLoading}
                            className="flex items-center gap-2 bg-brand-700 hover:bg-brand-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm shadow-brand-700/20 whitespace-nowrap"
                        >
                            {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                            Scarica PDF
                        </button>
                    </div>
                </div>
            )}

            {/* Filtri storico, solo nel tab STORICO */}
            {viewMode === 'STORICO' && (
                <section className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 mr-1 flex items-center gap-1">
                            <Calendar size={12}/> Periodo
                        </span>
                        {([
                            { id: '30d' as const, label: 'Ultimi 30 gg' },
                            { id: '90d' as const, label: 'Ultimi 90 gg' },
                            { id: '365d' as const, label: 'Ultimo anno' },
                            { id: 'custom' as const, label: 'Personalizzato' },
                        ]).map(p => (
                            <button
                                key={p.id}
                                onClick={() => applyPreset(p.id)}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                                    storicoPreset === p.id
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                                }`}
                            >{p.label}</button>
                        ))}
                    </div>
                    {storicoPreset === 'custom' && (
                        <div className="flex flex-wrap items-end gap-3 pt-1">
                            <label className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Dal</span>
                                <input
                                    type="date"
                                    value={storicoFrom}
                                    max={storicoTo}
                                    onChange={e => { setStoricoFrom(e.target.value); setStoricoPage(0); }}
                                    className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-700/30"
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Al</span>
                                <input
                                    type="date"
                                    value={storicoTo}
                                    min={storicoFrom}
                                    onChange={e => { setStoricoTo(e.target.value); setStoricoPage(0); }}
                                    className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-700/30"
                                />
                            </label>
                        </div>
                    )}
                    {/*!storicoLoading && (
                        <p className="text-[11px] text-slate-500">
                            {storicoTotalElements === 0
                                ? 'Nessun incarico nel periodo selezionato.'
                                : `${storicoTotalElements} incarico${storicoTotalElements === 1 ? '' : 'i'} dal ${storicoFrom} al ${storicoTo}` +
                                  (storicoTotalPages > 1
                                      ? ` · Pagina ${storicoPage + 1} di ${storicoTotalPages}`
                                      : '')}
                        </p>
                    )*/}
                </section>
            )}

            {/* Sezione Investigazioni */}
            <section>
                <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Briefcase size={16} /> {SECTION_LABEL[viewMode].incarichi}
                </h2>
                {viewMode === 'STORICO' && storicoLoading ? (
                    <div className="p-8 border border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-sm bg-white">
                        <Loader2 className="animate-spin mx-auto mb-2" size={20}/> Caricamento storico…
                    </div>
                ) : visibleIncarichi.length === 0 ? (
                    <div className="p-8 border border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-sm bg-white">
                        {EMPTY_MSG[viewMode].incarichi}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {visibleIncarichi.map(inc => (
                                <IncaricoCard
                                    key={inc.id}
                                    inc={inc}
                                    viewMode={viewMode}
                                    onClick={() => onSelectIncarico(Number(inc.id))}
                                    formatDateTime={formatDateTime}
                                />
                            ))}
                        </div>
                        {viewMode === 'STORICO' && storicoTotalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between gap-3">
                                <button
                                    onClick={() => setStoricoPage(p => Math.max(0, p - 1))}
                                    disabled={storicoPage === 0}
                                    className="px-3 py-2 text-sm font-bold border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                                ><ChevronLeft size={14}/> Indietro</button>
                                <span className="text-xs text-slate-500 font-semibold">
                                    Pagina {storicoPage + 1} di {storicoTotalPages}
                                </span>
                                <button
                                    onClick={() => setStoricoPage(p => Math.min(storicoTotalPages - 1, p + 1))}
                                    disabled={storicoPage >= storicoTotalPages - 1}
                                    className="px-3 py-2 text-sm font-bold border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                                >Avanti <ChevronRight size={14}/></button>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Sezione Turni Fiduciari */}
            <section>
                <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Shield size={16} /> {SECTION_LABEL[viewMode].turni}
                </h2>
                {filteredTurni.length === 0 ? (
                    <div className="p-8 border border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-sm bg-white">
                        {EMPTY_MSG[viewMode].turni}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredTurni.map(t => (
                            <TurnoCard
                                key={t.id}
                                t={t}
                                viewMode={viewMode}
                                onClick={() => onSelectTurno(Number(t.id))}
                                onRefresh={onRefresh}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Modale Urgenza */}
            {isUrgenzaOpen && (
                <UrgenzaModal
                    onClose={() => setIsUrgenzaOpen(false)}
                    onCreated={() => { setIsUrgenzaOpen(false); onUrgenzaCreated(); }}
                />
            )}

            {/*
              "Media da rivedere": riusa UploadSummaryModal con gli orfani. incaricoId=0
              fa passare null al backend, che usa l'incarico di ciascun media. Niente
              polling: i media sono già elaborati.
              
              Alla chiusura, i media ancora senza decisione vengono cestinati dopo conferma,
              così non si ripresentano all'infinito.
            */}
            <UploadSummaryModal
                isOpen={showOrfaniModal}
                onClose={async () => {
                    setShowOrfaniModal(false);
                    try {
                        // Rilegge lo stato dal BE: quelli ancora presenti sono i media senza decisione.
                        const list = await IncaricoService.getOrfani();
                        const remaining = Array.isArray(list) ? list : [];
                        if (remaining.length === 0) return;
                        const ok = window.confirm(
                            `Ci sono ${remaining.length} media senza decisione. ` +
                            `Chiudendo verranno cestinati definitivamente. Confermi?`
                        );
                        if (ok) {
                            await Promise.all(
                                remaining.map(r =>
                                    IncaricoService.deleteMediaByTempId(r.tempId).catch(() => null)
                                )
                            );
                            addToast(`${remaining.length} file cestinati`, 'info');
                        }
                    } finally {
                        loadOrfani();
                    }
                }}
                results={orfaniResults}
                incaricoId={0}
                context="collaboratore"
                onManualDateSave={async (mediaId, tempId, date, time) => {
                    const iso = `${date}T${time}:00`;
                    // Qui i media sono già persistiti e spesso senza temp_id: si usa mediaId,
                    // con tempId come fallback per gli upload in corso.
                    if (mediaId != null) {
                        await IncaricoService.confirmDateById(mediaId, iso);
                    } else {
                        await IncaricoService.confirmDate(tempId, iso);
                    }
                    addToast('Data salvata', 'success');
                }}
                onDiscard={async (_mediaId, tempId) => {
                    await IncaricoService.deleteMediaByTempId(tempId);
                    addToast('File scartato', 'info');
                }}
                onSubmitForApproval={async (items) => {
                    await IncaricoService.submitPendingApproval(null, items);
                    addToast('File inviati in approvazione', 'success');
                }}
            />
        </div>
    );
};
